function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

const PagedList = require('../entities/paged-list');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const PlanPipeline = require('../pipelines/plan-pipeline');

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

const AccessContext = require('./contexts/access-context');

const Plan = require('../entities/plan');

const {
  poll
} = require('../utils/poll');

const legacyFields = ['id', 'sid', 'desc'];
const fields = ['id', 'name', 'desc', 'settings(name,value,desc,value,group,schema,enums)', 'discoverable', 'enabled'];

class PlanRepo {
  constructor(client
  /*: ApiClient*/
  , id
  /*: planId*/
  , context
  /*: AccessContext*/
  ) {
    this.client = client;
    this.id = id;
    this.context = context;
  }

  details()
  /*: Promise<Plan>*/
  {
    const {
      propertyId
    } = this.context.serialize();
    const req = new this.client.Request(`/properties/${propertyId}/plans/${this.id}`, {}, fields);
    req.parse = Plan.fromJson;
    return this.client.get(req);
  }

  update(planFields
  /*: Object*/
  )
  /*: Promise<Plan>*/
  {
    const {
      propertyId
    } = this.context.serialize();
    const req = new this.client.Request(`/properties/${propertyId}/plans/${this.id}`, planFields, fields);
    req.serialize = Plan.toJson;
    req.parse = Plan.fromJson;
    return this.client.patch(req);
  }

  edit()
  /*: Promise<PlanPipeline>*/
  {
    return new PlanPipeline({
      context: {
        accessContext: this.context,
        client: this.client
      }
    }).load(this.id);
  }

  prioritize()
  /*: Promise<Plan>*/
  {
    const _this = this;

    return _call(function () {
      const {
        propertyId
      } = _this.context.serialize();

      const req = new _this.client.Request(`/properties/${propertyId}/plans/${_this.id}/prioritize`);
      return _await(_this.client.post(req), function () {
        return _this.client.properties.for(propertyId).plans.waitForIndex({
          enabled: null,
          discoverable: null,
          limit: 1
        }, result => result.items[0].id === _this.id, () => {
          throw new Error('Promoting took too long. Please check back later.');
        });
      });
    });
  }

  enable()
  /*: Promise<Plan>*/
  {
    return this.update({
      enabled: true
    });
  }

  disable()
  /*: Promise<Plan>*/
  {
    return this.update({
      enabled: false
    });
  }

  show()
  /*: Promise<Plan>*/
  {
    return this.update({
      discoverable: true
    });
  }

  hide()
  /*: Promise<Plan>*/
  {
    return this.update({
      discoverable: false
    });
  }

  waitForIndex({
    searchArgs = {
      enabled: null,
      discoverable: null
    },
    onTimeout = () => {
      throw new Error('Plan taking too long to be ready. Please check back in 10 minutes.');
    }
  } = {})
  /*: Promise<boolean>*/
  {
    const {
      propertyId
    } = this.context.serialize();
    return this.client.properties.for(propertyId).plans.waitForIndex(searchArgs, result => result.items.includes(plan => plan.id === this.id), onTimeout);
  }

}

class PlansRepo {
  constructor(client
  /*: ApiClient*/
  , parentContext
  /*: AccessContext*/
  ) {
    this.client = client;
    this.context = parentContext.clone();

    this.for = (id
    /*: planId*/
    ) => new PlanRepo(client, id, this.context);

    this.icanhas = this.for;
  } // For Legacy plans


  list()
  /*: (Promise<PagedList<Plan>>)*/
  {
    const {
      propertyId
    } = this.context.serialize();
    const req = new this.client.Request(`/properties/${propertyId}/plans`, {
      offset: 0,
      limit: 50
    }, legacyFields);
    req.parse = Plan.fromJson;
    return this.client.get(req);
  }

  search({
    /*: {
        name?: string, enabled?: ?boolean, discoverable?: ?boolean, limit?: number
      }*/

    name,
    enabled = true,
    discoverable = true,
    limit
  } = {})
  /*: Promise<PagedList<Plan>>*/
  {
    // Allow enabled and discoverable to default to true but be unsettable
    if (enabled === null) enabled = undefined; // eslint-disable-line no-param-reassign

    if (discoverable === null) discoverable = undefined; // eslint-disable-line no-param-reassign

    const {
      propertyId
    } = this.context.serialize();
    const path = `/properties/${propertyId}/plans/search`;
    const req = new this.client.Request(path, {
      name,
      enabled,
      discoverable,
      limit
    }, fields);
    req.parse = Plan.fromJson;
    return this.client.get(req);
  }

  listMine() {
    const _this2 = this;

    return _call(function () {
      const {
        propertyId
      } = _this2.context.serialize();

      return _await(_this2.client.properties.for(propertyId).getBusinessPlanSummary(), function (planSummary) {
        return planSummary.plans;
      });
    });
  }

  getSettings() {
    const req = new this.client.Request(`/plans/settings`);
    return this.client.get(req).then(response => response.items);
  }

  create(plan
  /*: Plan*/
  )
  /*: Promise<Plan>*/
  {
    const {
      propertyId
    } = this.context.serialize();
    const req = new this.client.Request(`/properties/${propertyId}/plans`, plan, fields);
    req.serialize = Plan.toJson;
    req.parse = Plan.fromJson;
    return this.client.post(req);
  }

  initialize()
  /*: Promise<PlanPipeline>*/
  {
    const _this3 = this;

    return _call(function () {
      const pipeline = new PlanPipeline({
        context: {
          accessContext: _this3.context,
          client: _this3.client
        }
      });
      return _await(pipeline.initialize(), function () {
        const plan = pipeline.getLocalState().plan;
        plan.discoverable = true;
        plan.enabled = true;
        return pipeline;
      });
    });
  }

  getBusinessPlan(businessId
  /*: string*/
  )
  /*: Promise<Plan>*/
  {
    const {
      propertyId
    } = this.context.serialize();
    const req = new this.client.Request(`/properties/${propertyId}/businesses/${businessId}/policy/plan`, {}, fields);
    req.parse = Plan.fromJson;
    return this.client.get(req);
  }

  waitForIndex(searchArgs
  /*: Object*/
  , compareFn
  /*: Function*/
  , onTimeout
  /*: Function*/
  )
  /*: Promise<boolean>*/
  {
    const _this4 = this;

    const predicate = _async(function () {
      return _await(_this4.search(searchArgs), compareFn);
    });

    return poll(predicate, undefined, 30000, onTimeout);
  } // For Legacy Plans


  getDefault()
  /*: Promise<Plan>*/
  {
    return this.search({
      limit: 1,
      enabled: true,
      discoverable: true
    }).then(({
      items
    }) => {
      if (!items.length) throw new Error('No plans available');
      return items[0];
    });
  }

}

module.exports = PlansRepo;